import { useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import { ApiError, api, type MailDelivery, type MailSettings } from './api.js';
import { dateTime, relative } from './format.js';

/**
 * Connecting the merchant's own mail server, so payment links come from them.
 *
 * The platform sends nothing from its own domain and this screen says so. A
 * payment request for someone else's business, arriving from wyocs.org, looks
 * exactly like a phishing attempt to both a spam filter and a careful customer.
 * Sent through the merchant's own server it is signed by their domain and lands
 * in the thread the customer already has with them.
 *
 * Two things this screen is careful about.
 *
 * The password is write-only. It is stored encrypted, never returned by the
 * API, and the field is left blank on an existing configuration - blank means
 * "keep the one you have", which is why it is labelled that way rather than
 * being pre-filled with a row of dots that a merchant would then have to guess
 * the meaning of.
 *
 * And saving is not arming. Settings that have never sent a message are shown
 * as unproven, and the server refuses to mail a customer from them. The only
 * thing that proves an SMTP configuration is a message that actually arrived,
 * because the usual failure - a server that authenticates happily and then
 * refuses the "from" address - passes every check short of sending.
 */

const BLANK = {
  host: '',
  port: 465,
  secure: true,
  username: '',
  password: '',
  from_name: '',
  from_email: '',
  reply_to: '',
};

export function MailSettingsView() {
  const [settings, setSettings] = useState<MailSettings | null>(null);
  const [form, setForm] = useState({ ...BLANK });
  const [deliveries, setDeliveries] = useState<MailDelivery[]>([]);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await api.mailSettings();
      setSettings(s);
      if (s.configured) {
        setForm({
          host: s.host ?? '',
          port: s.port ?? 465,
          secure: s.secure,
          username: s.username ?? '',
          // Never pre-filled: the server does not return it, and a placeholder
          // that looks like a password invites somebody to "keep" a blank one.
          password: '',
          from_name: s.fromName ?? '',
          from_email: s.fromEmail ?? '',
          reply_to: s.replyTo ?? '',
        });
        setTestTo((current) => current || s.fromEmail || '');
      }
      setDeliveries((await api.mailDeliveries().catch(() => ({ data: [] }))).data);
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.saveMailSettings({
        host: form.host,
        port: Number(form.port),
        secure: form.secure,
        username: form.username,
        ...(form.password ? { password: form.password } : {}),
        from_name: form.from_name,
        from_email: form.from_email,
        reply_to: form.reply_to || null,
      });
      setForm((f) => ({ ...f, password: '' }));
      setMsg({
        tone: 'warn',
        text: 'Saved. Nothing will be sent to a customer until you send yourself a test below and it arrives.',
      });
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.testMail(testTo);
      setMsg({ tone: 'ok', text: `Sent to ${testTo}. If it arrives, your setup is proven and payment links can go out.` });
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function disconnect(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      await api.disconnectMail();
      setForm({ ...BLANK });
      setMsg({ tone: 'warn', text: 'Disconnected. The stored password has been deleted.' });
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <Spinner label="Loading…" />;

  const verified = Boolean(settings.verifiedAt);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {!settings.available ? (
        <Note tone="danger" title="This server cannot hold a password yet">
          A mail password has to be stored so it can be handed to your mail server, which means it
          has to be encrypted rather than hashed. This server has no encryption key set, so it will
          refuse to store one rather than keep it in the clear. Tell us and we will set it.
        </Note>
      ) : settings.configured ? (
        <Note tone={verified ? 'ok' : 'warn'} title={verified ? 'Connected and proven' : 'Connected, but never proven'}>
          {verified ? (
            <>
              Payment links go out from <strong>{settings.fromEmail}</strong> through{' '}
              <strong>{settings.host}</strong>. Last test {relative(settings.lastTestAt ?? settings.verifiedAt!)}.
            </>
          ) : (
            <>
              These settings have never successfully sent anything, so customer email is refused.
              Send yourself a test below. A server that accepts your password and then rejects your
              "from" address is the usual failure, and only a real message finds it.
            </>
          )}
          {settings.lastError ? (
            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--wc-amber)' }}>
              Last error: {settings.lastError}
            </div>
          ) : null}
        </Note>
      ) : (
        <Note title="Send payment links from your own address">
          WyoCSPay sends nothing from its own domain, on purpose. A payment request for your
          business arriving from an address at wyocs.org looks like a phishing message to a spam
          filter and to your customer. Connect your own mail server and requests arrive from you,
          in the thread your customer already has with you, and replies reach you.
        </Note>
      )}

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <Panel title="Your mail server">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Server (SMTP host)">
            <input
              className="wc-input"
              value={form.host}
              placeholder="mail.yourcompany.com"
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
          <Field label="Port">
            <select
              className="wc-input"
              value={String(form.port)}
              onChange={(e) => {
                const port = Number(e.target.value);
                // 465 is TLS from the first byte; 587 upgrades with STARTTLS,
                // which the server then requires rather than merely offering.
                setForm({ ...form, port, secure: port === 465 });
              }}
              disabled={!settings.available}
            >
              <option value="465">465 - TLS (recommended)</option>
              <option value="587">587 - STARTTLS</option>
            </select>
          </Field>
          <Field label="Username">
            <input
              className="wc-input"
              value={form.username}
              placeholder="you@yourcompany.com"
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
          <Field
            label={settings.configured ? 'Password (blank keeps the current one)' : 'Password'}
            hint="Stored encrypted. Never shown again, not even to you."
          >
            <input
              className="wc-input"
              type="password"
              value={form.password}
              autoComplete="new-password"
              placeholder={settings.configured ? '••••••••' : ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
          <Field label="From name">
            <input
              className="wc-input"
              value={form.from_name}
              placeholder="Your Company Ltd"
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
          <Field label="From address" hint="Must be an address your server is allowed to send as.">
            <input
              className="wc-input"
              value={form.from_email}
              placeholder="billing@yourcompany.com"
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
          <Field label="Reply-to (optional)">
            <input
              className="wc-input"
              value={form.reply_to}
              placeholder="accounts@yourcompany.com"
              onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              disabled={!settings.available}
            />
          </Field>
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="wc-btn" onClick={() => void save()} disabled={busy || !settings.available}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {settings.configured ? (
            <button
              type="button"
              className="wc-btn wc-btn-secondary"
              onClick={() => void disconnect()}
              disabled={busy}
            >
              Disconnect
            </button>
          ) : null}
        </div>
      </Panel>

      {settings.configured ? (
        <Panel title="Prove it works">
          <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
            Sends a real message through your server. Until one succeeds, payment link email is
            refused - a configuration that has never sent anything is not a configuration, it is a
            guess.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Send the test to">
              <input
                className="wc-input"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@yourcompany.com"
              />
            </Field>
            <button type="button" className="wc-btn" onClick={() => void test()} disabled={busy || !testTo}>
              {busy ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </Panel>
      ) : null}

      <Panel title="What we have sent for you" padded={false}>
        {deliveries.length === 0 ? (
          <Empty>Nothing sent yet.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>What</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={dateTime(d.created_at)}>
                      {relative(d.created_at)}
                    </td>
                    <td style={{ fontSize: 13 }}>{d.to}</td>
                    <td style={{ fontSize: 13 }}>
                      {d.kind === 'payment_link' ? 'Payment link' : 'Test'}
                    </td>
                    <td>
                      <Badge tone={d.status === 'SENT' ? 'ok' : 'fail'}>{d.status}</Badge>
                      {d.error ? (
                        <div style={{ fontSize: 12, color: 'var(--wc-muted)', marginTop: 3 }}>{d.error}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>
    </div>
  );
}
